import { INVENTORY_COLUMN_REGISTRY, WAREHOUSE_DEFAULT_VISIBLE_COLUMNS, type InventoryColumnId } from '@/lib/views/columns/inventory'
import { useState } from 'react'
import type { WarehouseGrouping } from '@/lib/warehouse/url-state'

export type WarehouseColumnId = InventoryColumnId

export const WAREHOUSE_COLUMNS_STORAGE_KEY = 'warehouse-columns:product-table:v1'

function isColumn(value: unknown): value is WarehouseColumnId {
  return typeof value === 'string' && INVENTORY_COLUMN_REGISTRY.some((column) => column.id === value)
}

/** Consume the one legacy table preference while migrating it to the actual preset. */
export function readLegacyWarehouseColumns(): readonly WarehouseColumnId[] | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(WAREHOUSE_COLUMNS_STORAGE_KEY)
    if (raw === null) return null
    window.localStorage.removeItem(WAREHOUSE_COLUMNS_STORAGE_KEY)
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed) || !('columns' in parsed) || !Array.isArray(parsed.columns)) return null
    const values: unknown[] = parsed.columns
    const columns = values.filter(isColumn)
    if (columns.length !== values.length || columns.length === 0) return null
    return [...new Set(columns)]
  } catch {
    try { window.localStorage.removeItem(WAREHOUSE_COLUMNS_STORAGE_KEY) } catch { /* keep the table usable */ }
    return null
  }
}

export function ColumnOrderChooser({
  columns,
  grouping,
  onChange,
  onReset,
  saving = false,
}: {
  columns: readonly WarehouseColumnId[]
  onChange: (columns: readonly WarehouseColumnId[]) => void
  onReset?: () => void
  saving?: boolean
  grouping?: WarehouseGrouping
}) {
  const [open, setOpen] = useState(false)
  const registry = INVENTORY_COLUMN_REGISTRY.filter((column) => grouping === 'order' || !column.facets.includes('order'))
  const available = new Set(registry.map((column) => column.id))
  const visibleColumns = columns.filter((column) => available.has(column))
  const visible = new Set(visibleColumns)
  const byId = new Map(registry.map((column) => [column.id, column]))
  const listed = [
    ...visibleColumns.flatMap((id) => { const column = byId.get(id); return column === undefined ? [] : [column] }),
    ...registry.filter((column) => !visible.has(column.id)),
  ]
  const toggle = (id: WarehouseColumnId) => {
    onChange(visible.has(id) ? visibleColumns.filter((column) => column !== id) : [...visibleColumns, id])
  }
  const move = (index: number, delta: -1 | 1) => {
    const nextIndex = index + delta
    if (nextIndex < 0 || nextIndex >= visibleColumns.length) return
    const next = [...visibleColumns]
    const [item] = next.splice(index, 1)
    if (item === undefined) return
    next.splice(nextIndex, 0, item)
    onChange(next)
  }

  return (
    <details className="relative" onToggle={(event) => setOpen(event.currentTarget.open)}>
      <summary onClick={() => setOpen((value) => !value)} className="flex h-9 cursor-pointer list-none items-center rounded-full border border-rule px-3 text-xs font-medium text-ink-2 hover:bg-bone/60">
        Столбцы <span className="ml-1 rounded-full bg-bone px-1.5">{visibleColumns.length}/{registry.length}</span>
      </summary>
      {open ? <div className="absolute right-0 z-20 mt-2 w-72 rounded-lg border border-rule bg-paper p-3 shadow-lg">
        <div className="max-h-[min(70vh,32rem)] overflow-y-auto pr-1">
        <fieldset aria-label="Столбцы склада" className="space-y-1.5">
          <legend className="text-xs font-semibold text-ink">Показывать и упорядочить</legend>
          <p className="text-[11px] text-ink-muted">Стрелки меняют порядок видимых столбцов.</p>
          {listed.map((column) => {
            const index = visibleColumns.indexOf(column.id)
            return (
              <div key={column.id} className="flex items-center gap-1 text-xs text-ink-2">
                <label className="flex min-w-0 flex-1 items-center gap-2">
                  <input type="checkbox" checked={visible.has(column.id)} disabled={saving} onChange={() => toggle(column.id)} />
                  <span className="truncate">{column.label}</span>
                </label>
                {index >= 0 ? <>
                  <button type="button" aria-label={`Поднять ${column.label}`} disabled={saving || index === 0} onClick={() => move(index, -1)} className="rounded px-1 text-ink-muted hover:bg-bone disabled:opacity-30">▲</button>
                  <button type="button" aria-label={`Опустить ${column.label}`} disabled={saving || index === visibleColumns.length - 1} onClick={() => move(index, 1)} className="rounded px-1 text-ink-muted hover:bg-bone disabled:opacity-30">▼</button>
                </> : null}
              </div>
            )
          })}
        </fieldset>
        </div>
        {onReset ? <button type="button" disabled={saving} onClick={onReset} className="mt-3 text-xs text-ink-muted underline">Сбросить к стандартному</button> : null}
        {saving ? <span role="status" className="ml-2 text-[11px] text-ink-muted">Сохраняем…</span> : null}
      </div> : null}
    </details>
  )
}

export { WAREHOUSE_DEFAULT_VISIBLE_COLUMNS }
